import React, { useState, useEffect, useRef } from "react";
import "./Pairing.css";
import { FaPlus, FaTrash, FaTimes, FaLink } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeftRight } from "@fortawesome/free-solid-svg-icons";
import InfoTooltip from "../../../InfoTooltip";
import "./scorebox.css";
import TipButton from "../../../TipButton";
import StudentInfoTooltip from "../../../StudentInfoTooltip";
import { handleScoreChange } from "./helpers/scoreHelpers";
import CategoryTags from "./CategoryTags";

const Pairing = ({
  question,
  leftItems: initialLeftItems = [],
  rightItems: initialRightItems = [],
  pairs = [],
  score = 1,
  onChange,
  isStudentView = false,
  border,
  tip,
  questionCategories = [],
  showCategories = true,
  questionId,
  studentResponse,
  materialCategories,
  onFlip,
}) => {
  const [leftItems, setLeftItems] = useState(initialLeftItems);
  const [rightItems, setRightItems] = useState(initialRightItems);
  const [currentPair, setCurrentPair] = useState({ left: null, right: null });
  const [studentPairs, setStudentPairs] = useState(() => {
    if (isStudentView && studentResponse?.answer) {
      // Convert array of pairs to object format
      return studentResponse.answer.reduce((acc, pair) => {
        if (pair && pair.left && pair.right) {
          acc[pair.left] = pair.right;
        }
        return acc;
      }, {});
    }
    return {};
  });
  const [selectedStudentItems, setSelectedStudentItems] = useState({
    left: null,
    right: null,
  });
  const [newLeftItem, setNewLeftItem] = useState("");
  const [newRightItem, setNewRightItem] = useState("");
  const [isAddingLeft, setIsAddingLeft] = useState(false);
  const [isAddingRight, setIsAddingRight] = useState(false);

  useEffect(() => {
    if (!isStudentView) {
      setLeftItems(initialLeftItems);
      setRightItems(initialRightItems);
    } else {
      // Shuffle items for student view without mutating original lists
      const shuffledLeft = [...initialLeftItems].sort(
        () => Math.random() - 0.5
      );
      const shuffledRight = [...initialRightItems].sort(
        () => Math.random() - 0.5
      );
      setLeftItems(shuffledLeft);
      setRightItems(shuffledRight);

      // Initialize empty studentPairs
      setStudentPairs({});
    }
  }, [initialLeftItems, initialRightItems, isStudentView]);

  useEffect(() => {
    if (isStudentView && studentResponse?.answer) {
      setStudentPairs(
        studentResponse.answer.reduce((acc, pair) => {
          if (pair && pair.left && pair.right) {
            acc[pair.left] = pair.right;
          }
          return acc;
        }, {})
      );
    }
  }, [isStudentView, studentResponse]);

  const toggleAddMenuLeft = () => {
    setIsAddingLeft(true);
    setNewLeftItem("");
  };

  const toggleAddMenuRight = () => {
    setIsAddingRight(true);
    setNewRightItem("");
  };

  const handleSaveItem = (side) => {
    const item = side === "left" ? newLeftItem : newRightItem;
    if (item.trim()) {
      if (side === "left") {
        const updatedLeft = [...leftItems, item];
        setLeftItems(updatedLeft);
        onChange({ leftItems: updatedLeft });
        setIsAddingLeft(false);
      } else {
        const updatedRight = [...rightItems, item];
        setRightItems(updatedRight);
        onChange({ rightItems: updatedRight });
        setIsAddingRight(false);
      }
    }
  };

  const handleCancelAdd = (side) => {
    if (side === "left") {
      setIsAddingLeft(false);
    } else {
      setIsAddingRight(false);
    }
  };

  const handleAddMedia = (side) => {
    const mediaURL = prompt(`Enter media URL for the ${side} item:`);
    if (
      mediaURL &&
      ((side === "left" && !leftItems.includes(mediaURL)) ||
        (side === "right" && !rightItems.includes(mediaURL)))
    ) {
      if (side === "left") {
        const updatedLeft = [...leftItems, mediaURL];
        setLeftItems(updatedLeft);
        onChange({ leftItems: updatedLeft });
      } else {
        const updatedRight = [...rightItems, mediaURL];
        setRightItems(updatedRight);
        onChange({ rightItems: updatedRight });
      }
    }
  };

  const removeLeftItem = (item) => {
    const updatedLeft = leftItems.filter((left) => left !== item);
    const updatedPairs = pairs.filter((pair) => pair.left !== item);
    setLeftItems(updatedLeft);
    onChange({ leftItems: updatedLeft, pairs: updatedPairs });
  };

  const removeRightItem = (item) => {
    const updatedRight = rightItems.filter((right) => right !== item);
    const updatedPairs = pairs.filter((pair) => pair.right !== item);
    setRightItems(updatedRight);
    onChange({ rightItems: updatedRight, pairs: updatedPairs });
  };

  const handleItemClick = (side, item) => {
    if (!isStudentView) {
      setCurrentPair((prev) => ({
        ...prev,
        [side]: prev[side] === item ? null : item,
      }));
    } else {
      setSelectedStudentItems((prev) => {
        const newSelected = {
          ...prev,
          [side]: prev[side] === item ? null : item,
        };

        // If we have both a left and right item selected, create a pair
        if (newSelected.left && newSelected.right) {
          setStudentPairs((prevPairs) => {
            const newPairs = { ...prevPairs };
            // Clean up any existing "Not paired" entries
            Object.keys(newPairs).forEach((key) => {
              if (newPairs[key] === "Not paired" || key === "Not paired") {
                delete newPairs[key];
              }
            });
            // Create the new pair
            newPairs[newSelected.left] = newSelected.right;
            onChange({ studentPairs: newPairs });
            return newPairs;
          });
          // Reset selections after creating a pair
          return { left: null, right: null };
        }

        // If selecting same side again, replace the previous selection
        if (side === "left" && prev.left) {
          setStudentPairs((prevPairs) => {
            const newPairs = { ...prevPairs };
            // Clean up previous left item
            delete newPairs[prev.left];
            // Add new left item
            newPairs[item] = "Not paired";
            onChange({ studentPairs: newPairs });
            return newPairs;
          });
        } else if (side === "right" && prev.right) {
          setStudentPairs((prevPairs) => {
            const newPairs = { ...prevPairs };
            // Clean up previous right item
            Object.keys(newPairs).forEach((key) => {
              if (newPairs[key] === prev.right) {
                delete newPairs[key];
              }
            });
            // Add new right item
            newPairs["Not paired"] = item;
            onChange({ studentPairs: newPairs });
            return newPairs;
          });
        } else if (side === "left" && !prev.left) {
          // First left item selected
          setStudentPairs((prevPairs) => {
            const newPairs = { ...prevPairs };
            // Clean up any existing "Not paired" entries
            Object.keys(newPairs).forEach((key) => {
              if (newPairs[key] === "Not paired") {
                delete newPairs[key];
              }
            });
            newPairs[item] = "Not paired";
            onChange({ studentPairs: newPairs });
            return newPairs;
          });
        } else if (side === "right" && !prev.right) {
          // First right item selected
          setStudentPairs((prevPairs) => {
            const newPairs = { ...prevPairs };
            // Clean up any existing right "Not paired" entries
            delete newPairs["Not paired"];
            newPairs["Not paired"] = item;
            onChange({ studentPairs: newPairs });
            return newPairs;
          });
        }

        return newSelected;
      });
    }
  };

  const createPair = () => {
    const { left, right } = currentPair;
    if (left && right) {
      // Check if pair already exists
      if (pairs.some((pair) => pair.left === left || pair.right === right)) {
        alert("One of the items is already paired.");
        return;
      }
      const updatedPairs = [...pairs, { left, right }];
      setCurrentPair({ left: null, right: null });
      onChange({ pairs: updatedPairs });
    }
  };

  const removePair = (pairToRemove) => {
    const updatedPairs = pairs.filter(
      (pair) =>
        pair.left !== pairToRemove.left || pair.right !== pairToRemove.right
    );
    onChange({ pairs: updatedPairs });
  };

  const onScoreChange = handleScoreChange(onChange);

  // Add this new function to filter visible items
  const getVisibleItems = (items, side) => {
    if (!isStudentView) return items;

    const pairedItems = Object.entries(studentPairs).reduce(
      (acc, [left, right]) => {
        if (side === "left") {
          acc.push(left);
        } else if (right !== null) {
          acc.push(right);
        }
        return acc;
      },
      []
    );

    return items.filter((item) => !pairedItems.includes(item));
  };

  const calculatePointValue = () => {
    if (pairs.length === 0) return 0;
    return score / pairs.length;
  };

  const pointValue = calculatePointValue();

  return (
    <div className={`question-container ${border}`}>
      <div className="question-header">
        {isStudentView ? (
          <>
            <div className="audio-recorder-question">
              Match the items on the left with the corresponding items on the
              right.
            </div>
            <StudentInfoTooltip type="Pairing" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={question}
              onChange={(e) => onChange({ question: e.target.value })}
              placeholder="Enter your question here. Then, add items to pair on each side using the plus signs."
              className="question-input"
            />
            <InfoTooltip type="Pairing" />
          </>
        )}
      </div>
      <div className="pairing-content">
        <div className="pairing-column">
          <div className="column-header relative">
            <h4>List of items (A)</h4>
            {!isStudentView && (
              <div className="add-button-wrapper">
                <button
                  onClick={toggleAddMenuLeft}
                  className="add-button"
                  aria-label="Add Left Item"
                >
                  <FaPlus />
                </button>
              </div>
            )}
          </div>
          <ul>
            {getVisibleItems(leftItems, "left").map((item, index) => (
              <li
                key={index}
                className={`pairing-item ${
                  !isStudentView && currentPair.left === item ? "selected" : ""
                }`}
                onClick={() => handleItemClick("left", item)}
                role="button"
                tabIndex={0}
                aria-pressed={currentPair.left === item}
              >
                {item}
                {!isStudentView && (
                  <button
                    className="remove-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLeftItem(item);
                    }}
                    aria-label={`Remove left item ${item}`}
                  >
                    <FaTrash />
                  </button>
                )}
              </li>
            ))}
            {isAddingLeft && (
              <li>
                <input
                  type="text"
                  className="new-item-input"
                  value={newLeftItem}
                  onChange={(e) => setNewLeftItem(e.target.value)}
                  placeholder="Enter new item"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleSaveItem("left");
                  }}
                />
                <div className="new-item-actions">
                  <button
                    className="save-item-button"
                    onClick={() => handleSaveItem("left")}
                  >
                    Save
                  </button>
                  <button
                    className="cancel-item-button"
                    onClick={() => handleCancelAdd("left")}
                  >
                    Cancel
                  </button>
                </div>
              </li>
            )}
          </ul>
        </div>
        <div className="pairing-column">
          <div className="column-header relative">
            <h4>List of items (B)</h4>
            {!isStudentView && (
              <div className="add-button-wrapper">
                <button
                  onClick={toggleAddMenuRight}
                  className="add-button"
                  aria-label="Add Right Item"
                >
                  <FaPlus />
                </button>
              </div>
            )}
          </div>
          <ul>
            {getVisibleItems(rightItems, "right").map((item, index) => (
              <li
                key={index}
                className={`pairing-item ${
                  !isStudentView && currentPair.right === item ? "selected" : ""
                }`}
                onClick={() => handleItemClick("right", item)}
                role="button"
                tabIndex={0}
                aria-pressed={currentPair.right === item}
              >
                {item}
                {!isStudentView && (
                  <button
                    className="remove-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRightItem(item);
                    }}
                    aria-label={`Remove right item ${item}`}
                  >
                    <FaTrash />
                  </button>
                )}
              </li>
            ))}
            {isAddingRight && (
              <li>
                <input
                  type="text"
                  className="new-item-input"
                  value={newRightItem}
                  onChange={(e) => setNewRightItem(e.target.value)}
                  placeholder="Enter new item"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleSaveItem("right");
                  }}
                />
                <div className="new-item-actions">
                  <button
                    className="save-item-button"
                    onClick={() => handleSaveItem("right")}
                  >
                    Save
                  </button>
                  <button
                    className="cancel-item-button"
                    onClick={() => handleCancelAdd("right")}
                  >
                    Cancel
                  </button>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
      {!isStudentView && pairs.length > 0 && (
        <div className="pairs-list">
          <h4>Correct Pairs:</h4>
          <ul>
            {pairs.map((pair, index) => (
              <li key={index}>
                <span>
                  {pair.left} ↔ {pair.right}
                  <span className="point-value">
                    {window.matchMedia("(orientation: portrait)").matches
                      ? `+${pointValue.toFixed(2)} p`
                      : `+${pointValue.toFixed(2)} points`}
                  </span>
                </span>
                <button
                  onClick={() => removePair(pair)}
                  className="remove-pair-button"
                  aria-label={`Remove pair ${pair.left} and ${pair.right}`}
                >
                  <FaTimes />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isStudentView && Object.keys(studentPairs).length > 0 && (
        <div className="student-pairs">
          <h4>Your Pairs:</h4>
          <ul>
            {Object.entries(studentPairs).map(
              ([leftItem, rightItem], index) => (
                <li key={index}>
                  {leftItem} ↔ {rightItem || "Not paired"}
                </li>
              )
            )}
          </ul>
        </div>
      )}
      <div className="category-tags-container">
        <CategoryTags
          categories={questionCategories}
          onChange={(newCategories) =>
            onChange({
              questionCategories: newCategories,
            })
          }
          isStudentView={isStudentView}
          showCategories={showCategories}
        />
      </div>
      <div className="question-footer">
        <div className="question-footer-flex">
          <div className="button-wrapper">
            {!isStudentView && (
              <button
                onClick={createPair}
                className="question-footer-button"
                aria-label="Create Pair"
              >
                <FaLink size={12} /> Make Pair
              </button>
            )}
            {isStudentView && (
              <button
                onClick={() => setStudentPairs({})}
                className="question-footer-button"
              >
                Reset Pairs
              </button>
            )}
          </div>
          <TipButton
            tip={tip}
            onChange={onChange}
            isStudentView={isStudentView}
            onFlip={onFlip}
          />
          <div className="scorebox-container">
            <label htmlFor="score">Score:</label>
            <input
              type="number"
              id="score"
              value={score}
              onChange={onScoreChange}
              min="0"
              step="1"
              readOnly={isStudentView}
              className={isStudentView ? "student-view" : ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pairing;
